
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { SurveyHeader } from "./SurveyHeader";
import { ConsentForm } from "./ConsentForm";
import { SurveyQuestions } from "./SurveyQuestions";
import { useSurveyForm } from "@/hooks/useSurveyForm";
import type { Database } from "@/integrations/supabase/types";

type SupportedLanguage = Database["public"]["Enums"]["supported_language"];
type ParticipantGroup = Database["public"]["Enums"]["participant_group"];
type Survey = Database["public"]["Tables"]["surveys"]["Row"];

interface SurveyFormProps {
  survey: Survey;
  participantGroup?: ParticipantGroup | null;
  previousConsent?: {
    studyConsent: boolean;
    dataConsent: boolean;
    phoneNumber: string;
  } | null;
  previousAnswers?: Record<string, string> | null;
  currentStep?: number;
  initialLanguage?: SupportedLanguage; // Add initialLanguage prop
  onSubmit: (answers: Record<string, string>, language: SupportedLanguage, phoneNumber?: string, responseTime?: number, studyConsent?: boolean, dataConsent?: boolean) => Promise<void>;
}

// RTL languages
const RTL_LANGUAGES = ["ar", "fa", "prs"];

export const SurveyForm = ({ 
  survey, 
  participantGroup, 
  previousConsent, 
  previousAnswers,
  currentStep = 0,
  initialLanguage = "sv", // Default to Swedish if not provided
  onSubmit 
}: SurveyFormProps) => {
  // Use initialLanguage if provided, otherwise default to Swedish
  const [language, setLanguage] = useState<SupportedLanguage>(initialLanguage);
  const isRtl = RTL_LANGUAGES.includes(language);
  
  // Update language if initialLanguage prop changes
  useEffect(() => {
    if (initialLanguage && initialLanguage !== language) {
      setLanguage(initialLanguage);
      console.log(`Updating language from prop: ${initialLanguage}`);
    }
  }, [initialLanguage]);

  const {
    answers,
    isSubmitting,
    showQuestions,
    handleAnswerChange,
    handleConsentContinue,
    handleSubmit,
    handleLanguageChange
  } = useSurveyForm({
    survey,
    language,
    participantGroup,
    previousConsent,
    previousAnswers,
    currentStep,
    onSubmit
  });

  const onLanguageChange = (newLanguage: SupportedLanguage) => {
    console.log(`Language changed from ${language} to ${newLanguage}`);
    setLanguage(newLanguage);
    handleLanguageChange(newLanguage);
  };

  // Debug survey questions structure
  console.log('Survey questions structure:', survey.questions);
  console.log('Current language:', language);
  
  const hasQuestions = survey.questions && 
                       survey.questions[language] && 
                       Array.isArray(survey.questions[language]) && 
                       (survey.questions[language] as any[]).length > 0;

  if (!hasQuestions) {
    console.error('Invalid survey questions format:', survey.questions);
    return (
      <Card className="w-full max-w-2xl p-6 space-y-6" dir={isRtl ? "rtl" : "ltr"}>
        <SurveyHeader 
          survey={survey} 
          language={language} 
          onLanguageChange={onLanguageChange} 
        />
        <div className="text-center">
          <p>No questions available for this language.</p>
          <p className="text-sm text-muted-foreground mt-2">
            Language: {language}, Survey ID: {survey.id}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card 
      className="w-full max-w-2xl p-6 space-y-6 animate-fadeIn" 
      dir={isRtl ? "rtl" : "ltr"}
    >
      <SurveyHeader 
        survey={survey} 
        language={language} 
        onLanguageChange={onLanguageChange} 
      />

      {!showQuestions ? (
        <ConsentForm
          language={language}
          onContinue={handleConsentContinue}
          previousConsent={previousConsent}
        />
      ) : (
        <SurveyQuestions
          survey={survey}
          language={language}
          answers={answers}
          isSubmitting={isSubmitting}
          onAnswerChange={handleAnswerChange}
          onSubmit={handleSubmit}
          participantGroup={participantGroup}
          previousAnswers={previousAnswers} // Pass previousAnswers
        />
      )}
    </Card>
  );
};
